<template>
  <div class="rounded-md border border-obsidian-100 bg-obsidian-50 px-3 py-2 text-sm">
    <Collapsible
      :model-value="open"
      variant="compact"
      @update:model-value="emit('update:open', $event)"
    >
      <template #title>
        <span class="flex items-center gap-1.5">
          <PlusIcon class="size-3.5" />
          {{ t('moreOpenAmounts', { payer: payerName || t('unknownPayer'), count: items.length }) }}
        </span>
      </template>

      <div v-if="isLoading" class="flex justify-center py-3">
        <Spinner class="text-hazel-600" />
      </div>
      <p v-else-if="!items.length" class="py-2 text-obsidian-500">{{ t('noOpenAmounts') }}</p>
      <ul v-else class="max-h-56 space-y-1.5 overflow-y-auto py-1">
        <li v-for="item in items" :key="item.key" data-testid="bank-statement-payer-open-amount">
          <Checkbox
            :model-value="selectedKeys.includes(item.key)"
            :disabled="isBlocked(item)"
            @update:model-value="handleToggle(item, $event)"
          >
            <span class="flex w-full min-w-0 items-baseline justify-between gap-3">
              <span class="min-w-0 truncate">
                <span v-if="item.booking" class="font-medium">
                  {{ t('bookingLabel', { number: item.booking.bookingNumber }) }}
                </span>
                <span v-if="item.invoice?.completeInvoiceNumber" class="ml-2">
                  {{ item.invoice.completeInvoiceNumber }}
                </span>
                <span v-if="item.dueSince" class="ml-2 text-obsidian-500">
                  {{ t('dueSince', { date: formatDate(item.dueSince, DateTime.DATE_SHORT) }) }}
                </span>
              </span>
              <span class="shrink-0 tabular-nums">{{ formatCurrency(item.amount) }}</span>
            </span>
          </Checkbox>
        </li>
      </ul>
    </Collapsible>

    <div
      v-if="selectedKeys.length"
      data-testid="bank-statement-payer-sum"
      class="mt-2 space-y-1 border-t border-obsidian-200 pt-2 tabular-nums"
    >
      <p class="flex justify-between">
        <span>{{ t('sum', { count: selectedCount }) }}</span>
        <span class="font-semibold">{{ formatCurrency(selectedSum) }}</span>
      </p>
      <p class="flex justify-between" :class="difference === 0 ? 'text-green-800' : undefined">
        <span>{{ t('difference') }}</span>
        <span class="font-semibold">{{ formatCurrency(difference) }}</span>
      </p>
      <p v-if="difference > 0" class="text-xs text-obsidian-500">{{ t('remainderHint') }}</p>
      <p v-else-if="difference < 0" class="text-xs text-obsidian-500">{{ t('shortHint') }}</p>
    </div>
  </div>
</template>

<script lang="ts" setup>
import Checkbox from '@/components/Checkbox.vue';
import Collapsible from '@/components/Collapsible.vue';
import Spinner from '@/components/icons/Spinner.vue';
import { formatCurrency } from '@/utils/format-numbers';
import { formatDate } from '@/hooks/use-formatted-date';
import {
  doOpenAmountsOverlap,
  getAmountDifference,
  sumOpenAmounts,
} from '@/api/bank-statements.service';
import { DateTime } from 'luxon';
import { PlusIcon } from 'lucide-vue-next';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BankStatementOpenAmountDto } from '@carvia/ros-client-types';

const props = defineProps<{
  entryAmount: number;
  payerName: string | null;
  /** The payer's other open amounts, oldest due first. */
  items: BankStatementOpenAmountDto[];
  /** The target chosen in the picker, if any; counted in the sum. */
  anchor: BankStatementOpenAmountDto | null;
  selectedKeys: string[];
  open: boolean;
  isLoading?: boolean;
}>();

const emit = defineEmits<{
  (e: 'update:selectedKeys', value: string[]): void;
  (e: 'update:open', value: boolean): void;
}>();

const { t } = useI18n();

const selectedItems = computed(() =>
  props.items.filter((item) => props.selectedKeys.includes(item.key)),
);
const selectedCount = computed(() => selectedItems.value.length + (props.anchor ? 1 : 0));
const selectedSum = computed(() =>
  sumOpenAmounts(props.anchor ? [props.anchor, ...selectedItems.value] : selectedItems.value),
);
const difference = computed(() => getAmountDifference(props.entryAmount, selectedSum.value));

// A booking remaining to pay and its own invoices are the same money.
const isBlocked = (item: BankStatementOpenAmountDto) =>
  !props.selectedKeys.includes(item.key) &&
  [props.anchor, ...selectedItems.value].some(
    (taken) => !!taken && doOpenAmountsOverlap(taken, item),
  );

const handleToggle = (item: BankStatementOpenAmountDto, checked: boolean) => {
  const others = props.selectedKeys.filter((key) => key !== item.key);
  emit('update:selectedKeys', checked ? [...others, item.key] : others);
};
</script>

<i18n lang="json">
{
  "en": {
    "moreOpenAmounts": "More open items from {payer} ({count})",
    "unknownPayer": "this payer",
    "noOpenAmounts": "No further open items.",
    "bookingLabel": "Booking {number}",
    "dueSince": "due {date}",
    "sum": "Sum of {count} items",
    "difference": "Difference to the amount",
    "remainderHint": "The rest stays unallocated on the payment and shows up in Problems.",
    "shortHint": "Oldest due first — the last item is paid only partially."
  },
  "de": {
    "moreOpenAmounts": "Weitere offene Posten von {payer} ({count})",
    "unknownPayer": "diesem Zahler",
    "noOpenAmounts": "Keine weiteren offenen Posten.",
    "bookingLabel": "Buchung {number}",
    "dueSince": "fällig {date}",
    "sum": "Summe aus {count} Posten",
    "difference": "Differenz zum Betrag",
    "remainderHint": "Der Rest bleibt unzugeordnet auf der Zahlung und erscheint unter Probleme.",
    "shortHint": "Älteste Fälligkeit zuerst — der letzte Posten wird nur teilweise bezahlt."
  },
  "es": {
    "moreOpenAmounts": "Más partidas abiertas de {payer} ({count})",
    "unknownPayer": "este pagador",
    "noOpenAmounts": "No hay más partidas abiertas.",
    "bookingLabel": "Reserva {number}",
    "dueSince": "vence {date}",
    "sum": "Suma de {count} partidas",
    "difference": "Diferencia con el importe",
    "remainderHint": "El resto queda sin asignar en el pago y aparece en Problemas.",
    "shortHint": "Primero el vencimiento más antiguo; la última partida se paga solo en parte."
  }
}
</i18n>
